// 261005 E1-P2 — hàm THUẦN của chặng ⑤ Kịch bản (2 cột). KHÔNG import antd / react (jest của react-scripts không load được antd v6).
// Chip chính + dấu phụ, lọc, chọn dải liền, lý do "Cần xem", chữ đáy. Component chỉ vẽ lại kết quả.
import type { EpisodeScriptRecord, OutlineEpisode } from '../../../types/adaptation';
import type { WorkbenchEpisode, WorkbenchView } from '../../../types/adaptationWorkbench';
import { ReaderIssueView, ReaderRewrite, SeasonReaderCardView } from '../seasonReaderView';
import { friendlyJobNote } from './jobErrorText';

// ─── Chip chính + dấu phụ ─────────────────────────────────────────────────

export type ChipKey = 'review' | 'none' | 'draft';
export interface EpisodeChip { key: ChipKey; label: string; tone: 'warn' | 'todo' | 'ok' }

/** Chip chính = việc cần làm. Cần xem > Chưa viết > Đã viết. Tập FAILED luôn là "Cần xem". */
export const episodeChip = (ep: WorkbenchEpisode): EpisodeChip => {
  // 261015 P5d (D2): `criticBlocked` (giám khảo C2 REPAIR_REJECTED/UNRESOLVED còn CRITICAL hiện hành) coi như `criticErrors > 0` — bản lưu còn lỗi nặng KHÔNG được xanh "Đã viết".
  if (ep.written === 'failed' || ep.criticErrors > 0 || ep.criticBlocked === true || ep.readerBlocking > 0 || ep.continuity === 'dirty' || !ep.inOutline) {
    return { key: 'review', label: 'Cần xem', tone: 'warn' };
  }
  if (ep.written === 'none') return { key: 'none', label: 'Chưa viết', tone: 'todo' };
  return { key: 'draft', label: 'Đã viết', tone: 'ok' };
};

export interface EpisodeBadge { key: string; label: string; tip: string }

export const episodeBadges = (ep: WorkbenchEpisode): EpisodeBadge[] => {
  const out: EpisodeBadge[] = [];
  if (ep.handoff === 'up_to_date') out.push({ key: 'chapters', label: 'Chapters', tip: 'Đã đẩy, Chapters khớp bản này' });
  else if (ep.handoff === 'changed_since_handoff') {
    out.push({ key: 'chapters?', label: 'Chapters ?', tip: 'Đã sửa sau lần đẩy — mở Đẩy sang Chapters để xem tập này có được cập nhật không' });
  } else if (ep.handoff === 'unknown_legacy') {
    out.push({ key: 'chapters?', label: 'Chapters ?', tip: 'Đã đẩy trước khi có dấu đối chiếu — chưa rõ còn khớp' });
  }
  if (ep.lastOrigin === 'CHAT_EDIT') out.push({ key: 'origin', label: 'sửa bằng AI', tip: 'Lần ghi gần nhất là sửa bằng AI' });
  else if (ep.lastOrigin === 'MANUAL_EDIT') out.push({ key: 'origin', label: 'sửa tay', tip: 'Lần ghi gần nhất là sửa tay' });
  else if (ep.lastOrigin === 'RESTORE') out.push({ key: 'origin', label: 'đã khôi phục', tip: 'Lần ghi gần nhất là khôi phục phiên bản cũ' });
  return out;
};

// ─── AI đọc cả mùa theo tập ───────────────────────────────────────────────

/** Chỉ khi cổng đang BLOCKED — khớp luật đếm `readerBlocking` của backend. */
export const readerIssuesFor = (view: SeasonReaderCardView | null | undefined): ReaderIssueView[] =>
  view && view.state === 'BLOCKED' ? view.blocking : [];

/** Số ghi chú không chặn của tập (gợi ý; lỗi ở STALE / NOT_COVERED / OVERRIDDEN). */
export const readerNotesFor = (view: SeasonReaderCardView | null | undefined, ep: number): number => {
  if (!view) return 0;
  const pool = view.state === 'BLOCKED' ? view.advisory : [...view.blocking, ...view.advisory];
  return pool.filter((i) => i.episode === ep).length;
};

export type ReviewKind = 'failed' | 'critic' | 'critic_blocked' | 'continuity' | 'reader' | 'reader_summary' | 'orphan';
export interface ReviewReason {
  kind: ReviewKind;
  text: string;
  /** Id ỨNG VIÊN của chip BE (luật `episodeFixChat.ts` `issuesForEpisode`) — có ⇒ "Nhờ AI sửa" tick sẵn; không ⇒ "Hỏi trợ lý". */
  issueId?: string;
  rewrite?: ReaderRewrite;
  rewriteFrom?: ReaderRewrite;
}

/**
 * 261015 P2b-2 (Q1) — ERROR còn SỐNG của tập, đọc từ đúng danh sách `violations` mà GET trả (đã có bản tính lại): bỏ phần tử `superseded` (bản lưu đã bị thay — BE gắn `retired`, không có chip)
 * và mọi phần tử không phải `defect` (retired/accepted/…). Giữ CHỈ SỐ GỐC trong mảng (BE đánh `violation:<chỉ số>:<gate>` trên mảng đầy đủ). Danh sách chưa có / không chứa ERROR nào ⇒ `null` (caller rơi về số BE).
 */
export const liveErrorsOf = (script: Pick<EpisodeScriptRecord, 'violations'> | null | undefined): Array<{ v: NonNullable<EpisodeScriptRecord['violations']>[number]; idx: number }> | null => {
  if (!Array.isArray(script?.violations)) return null;
  // Danh sách không có ERROR nào (kể cả đã bị thay) ⇒ không đủ dữ liệu để phản bác số BE (vd workbench và danh sách tải lệch nhau) ⇒ rơi về số BE.
  if (!script!.violations!.some((v) => v?.severity === 'ERROR')) return null;
  const out: Array<{ v: NonNullable<EpisodeScriptRecord['violations']>[number]; idx: number }> = [];
  script!.violations!.forEach((v, idx) => { if (v?.severity === 'ERROR' && v.superseded !== true && (v.kind ?? 'defect') === 'defect') out.push({ v, idx }); });
  return out;
};

/**
 * Số lỗi chặn của tập theo danh sách GET (sống) thay cho số BE đếm trên ảnh chụp lúc viết (BE `criticErrors` đếm cả bản lưu đã bị thay và không thấy ERROR mới do tính lại lúc đọc).
 * Chưa có danh sách ⇒ giữ nguyên số BE. Không đổi trường nào khác.
 */
export const withLiveErrors = (ep: WorkbenchEpisode, script: Pick<EpisodeScriptRecord, 'violations'> | null | undefined): WorkbenchEpisode => {
  const live = liveErrorsOf(script);
  return live ? { ...ep, criticErrors: live.length } : ep;
};

export const episodeReviewReasons = (
  ep: WorkbenchEpisode,
  script: Pick<EpisodeScriptRecord, 'violations'> | null | undefined,
  readerIssues: ReaderIssueView[],
): ReviewReason[] => {
  const out: ReviewReason[] = [];
  if (ep.written === 'failed') {
    out.push({
      kind: 'failed', text: `Viết hỏng${friendlyJobNote(ep.error) ? `: ${friendlyJobNote(ep.error)}` : ''}`,
      rewrite: { from: ep.ep, to: ep.ep, force: true, label: `Viết lại tập ${ep.ep}` },
    });
  }
  const errs = liveErrorsOf(script);
  const criticCount = errs ? errs.length : ep.criticErrors;
  if (criticCount > 0) {
    // Giữ CHỈ SỐ GỐC trong mảng; gate CRITIC bị BE bỏ khỏi chip ⇒ không id.
    if (errs && errs.length) {
      errs.forEach(({ v, idx }) => out.push({ kind: 'critic', text: v.message, ...(v.gate !== 'CRITIC' ? { issueId: `violation:${idx}:${v.gate}` } : {}) }));
    } else out.push({ kind: 'critic', text: `${criticCount} lỗi của người chấm` });
  }
  // 261015 P5d (D2): chỉ thông tin (không nút): lỗi nặng của giám khảo nằm ở chip từng cảnh bên dưới (có "Phương án"/Trợ lý AI); gỡ chặn = sửa cảnh lỗi hoặc viết lại tập.
  if (ep.criticBlocked === true) {
    out.push({ kind: 'critic_blocked', text: 'Giám khảo AI còn lỗi nặng (CRITICAL) chưa xử lý xong — tập này chưa đẩy sang Chapters được. Sửa các cảnh lỗi (xem cảnh báo bên dưới) hoặc viết lại tập.' });
  }
  if (ep.continuity === 'dirty') out.push({ kind: 'continuity', text: ep.continuityReason ?? 'Nối tập chưa khớp' });
  const mine = readerIssues.filter((i) => i.episode === ep.ep);
  mine.forEach((i) => out.push({ kind: 'reader', text: i.problem, ...(i.unitExact ? { issueId: i.id } : {}), rewrite: i.rewriteOne ?? undefined, rewriteFrom: i.rewriteFrom ?? undefined }));
  if (ep.readerBlocking > 0 && mine.length === 0) {
    out.push({ kind: 'reader_summary', text: `AI đọc cả mùa chặn ${ep.readerBlocking} lỗi ở tập này — bấm Xem để đọc chi tiết` });
  }
  if (!ep.inOutline) out.push({ kind: 'orphan', text: 'Tập này có kịch bản nhưng không nằm trong danh sách tập của khung xương' });
  return out;
};

export type ReviewAction =
  | { via: 'writeOne'; ep: number }
  | { via: 'range'; opts: { from: number; to: number; force: true } }
  | { via: 'openReader' }
  | null;

/** Nút của một dòng "Cần xem" gọi gì. `which` chọn "Viết lại tập" (one) hay "tuần tự từ tập" (from). */
export const reviewAction = (reason: ReviewReason, ep: number, which: 'one' | 'from' = 'one'): ReviewAction => {
  if (reason.kind === 'failed') return { via: 'writeOne', ep };
  if (reason.kind === 'reader_summary') return { via: 'openReader' };
  if (reason.kind === 'reader') {
    const rw = which === 'from' ? (reason.rewriteFrom ?? reason.rewrite) : (reason.rewrite ?? reason.rewriteFrom);
    if (rw) return { via: 'range', opts: { from: rw.from, to: rw.to, force: true } };
  }
  return null;
};

// ─── Lọc ──────────────────────────────────────────────────────────────────

export type EpisodeFilter = 'all' | 'review' | 'none' | 'draft' | 'chapters' | 'new';

/** `ctx.lockedThrough` (M của `appendRun`): `'new'` = tập `> M`. Không có ctx ⇒ `'new'` không khớp tập nào. */
export interface FilterCtx { lockedThrough?: number }

export const matchesFilter = (ep: WorkbenchEpisode, f: EpisodeFilter, ctx?: FilterCtx): boolean => {
  switch (f) {
    case 'all': return true;
    case 'new': return typeof ctx?.lockedThrough === 'number' && ep.ep > ctx.lockedThrough;
    case 'chapters': return ep.handoff === 'up_to_date';
    default: return episodeChip(ep).key === f;
  }
};

export const filterCounts = (eps: WorkbenchEpisode[], ctx?: FilterCtx): Record<EpisodeFilter, number> => ({
  all: eps.length,
  new: eps.filter((e) => matchesFilter(e, 'new', ctx)).length,
  review: eps.filter((e) => matchesFilter(e, 'review')).length,
  none: eps.filter((e) => matchesFilter(e, 'none')).length,
  draft: eps.filter((e) => matchesFilter(e, 'draft')).length,
  chapters: eps.filter((e) => matchesFilter(e, 'chapters')).length,
});

// ─── Chọn dải liền + viết ─────────────────────────────────────────────────

/** Thứ tự chuẩn của chọn dải: tập trong khung xương, tăng dần, không orphan, không phụ thuộc bộ lọc. */
export const selectionOrder = (outline: { episodes: Array<{ ep: number }> } | null | undefined): number[] =>
  (outline?.episodes ?? []).map((e) => e.ep).sort((a, b) => a - b);

type ScriptStatusLike = { episode: number; status: string };
export type SplitModeLike = 'chapter' | 'range' | null | undefined;

const isDraft = (scripts: ScriptStatusLike[], ep: number): boolean => scripts.some((s) => s.episode === ep && s.status === 'DRAFT');

/**
 * Truyện `range` viết tuần tự: backend đòi tập e>1 có tập e−1 là DRAFT (`outlineWritableGuard.ts`) — FE chỉ chặn ca CHẮC CHẮN bị từ chối.
 * Trả số tập `prev` còn thiếu, hoặc `null` nếu không chặn.
 */
export const writeOneBlocker = (ep: number, scripts: ScriptStatusLike[], splitMode: SplitModeLike): number | null =>
  splitMode === 'range' && ep > 1 && !isDraft(scripts, ep - 1) ? ep - 1 : null;

export type RangeWritePlan =
  | { kind: 'none' }
  | { kind: 'gap' }
  | { kind: 'blocked'; reason: 'prev_missing'; prev: number }
  | { kind: 'range'; from: number; to: number; todo: number; skipped: number };

export const rangeWritePlan = (selected: number[], order: number[], scripts: ScriptStatusLike[], splitMode?: SplitModeLike): RangeWritePlan => {
  if (selected.length === 0) return { kind: 'none' };
  const idx = Array.from(new Set(selected)).map((e) => order.indexOf(e));
  if (idx.some((i) => i < 0)) return { kind: 'gap' };
  const lo = Math.min(...idx);
  const hi = Math.max(...idx);
  if (hi - lo + 1 !== idx.length) return { kind: 'gap' };
  const span = order.slice(lo, hi + 1);
  const todoEps = span.filter((e) => !isDraft(scripts, e));
  const plan = { kind: 'range' as const, from: span[0], to: span[span.length - 1], todo: todoEps.length, skipped: span.length - todoEps.length };
  if (todoEps.length > 0) {
    const prev = writeOneBlocker(todoEps[0], scripts, splitMode);
    if (prev !== null) return { kind: 'blocked', reason: 'prev_missing', prev };
  }
  return plan;
};

export const toggleSelect = (
  prev: number[], ep: number, shift: boolean, anchor: number | null, order: number[],
): { selected: number[]; anchor: number } => {
  const a = anchor !== null ? order.indexOf(anchor) : -1;
  const b = order.indexOf(ep);
  if (shift && a >= 0 && b >= 0) {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    return { selected: order.slice(lo, hi + 1), anchor: anchor as number };
  }
  if (shift && anchor === null) return { selected: [ep], anchor: ep };
  const has = prev.includes(ep);
  const next = has ? prev.filter((x) => x !== ep) : [...prev, ep];
  return { selected: next.sort((x, y) => x - y), anchor: ep };
};

/** Số tập đã chọn nhưng đang bị bộ lọc ẩn. */
export const hiddenSelectedCount = (selected: number[], visible: number[]): number => {
  const v = new Set(visible);
  return selected.filter((e) => !v.has(e)).length;
};

export const selectionLabel = (selected: number[], hidden: number): string => {
  if (selected.length === 0) return '';
  const s = [...selected].sort((a, b) => a - b);
  const contiguousByNumber = s[s.length - 1] - s[0] + 1 === s.length;
  const range = contiguousByNumber && s.length > 1 ? ` (${s[0]}–${s[s.length - 1]})` : '';
  return `Đã chọn ${s.length} tập${range}${hidden > 0 ? ` · ${hidden} tập đang ẩn do bộ lọc` : ''}`;
};

// ─── URL + tập mở mặc định ────────────────────────────────────────────────

export const parseEpParam = (v: string | null | undefined): number | null => {
  if (v === null || v === undefined || !/^\d+$/.test(v)) return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

export const defaultEpisode = (urlEp: number | null, rows: Array<{ ep: number }>, eps: WorkbenchEpisode[]): number | null => {
  if (rows.length === 0) return null;
  if (urlEp !== null && rows.some((r) => r.ep === urlEp)) return urlEp;
  const inRows = (e: WorkbenchEpisode) => rows.some((r) => r.ep === e.ep);
  const firstReview = eps.find((e) => inRows(e) && episodeChip(e).key === 'review');
  if (firstReview) return firstReview.ep;
  const firstNone = eps.find((e) => inRows(e) && episodeChip(e).key === 'none');
  if (firstNone) return firstNone.ep;
  return rows[0].ep;
};

export const episodeLabel = (row: Pick<OutlineEpisode, 'ep' | 'synopsis'>, wb: Pick<WorkbenchEpisode, 'title'> | undefined, max = 60): string => {
  const title = wb?.title?.trim();
  if (title) return `${row.ep}. ${title}`;
  const syn = (row.synopsis ?? '').trim();
  return `${row.ep}. ${syn.length > max ? `${syn.slice(0, max)}…` : syn}`;
};

// ─── Chữ ở đáy ────────────────────────────────────────────────────────────

export const readerFooter = (
  view: SeasonReaderCardView | null | undefined,
  wb: WorkbenchView['seasonReader'] | undefined,
): string => {
  const counts = (blocking: number, advisory: number) => `${blocking > 0 ? ` · ${blocking} chặn` : ''}${advisory > 0 ? ` · ${advisory} gợi ý` : ''}`;
  if (view) return `AI đọc cả mùa: ${view.label}${view.rangeText ? ` · ${view.rangeText}` : ''}${counts(view.blocking.length, view.advisory.length)}`;
  if (wb) return `AI đọc cả mùa: ${wb.state}${counts(wb.blocking, wb.advisory)}`;
  return 'AI đọc cả mùa: chưa có kịch bản để đọc';
};

export const handoffFooter = (
  h: { linkedCount: number; pendingCount: number; maybeCount: number },
  maybeMessage: string | undefined,
): { text: string; warning: string | null } => ({
  text: `${h.linkedCount} tập đã khớp · ${h.pendingCount} tập cần đẩy`,
  warning: h.maybeCount > 0
    ? (maybeMessage ?? `${h.maybeCount} tập đã sửa sau lần đẩy — mở 'Đẩy sang Chapters' để xem tập nào sẽ cập nhật`)
    : null,
});

/**
 * 261015 P4a — dòng "Tự kiểm lúc viết" ở đầu khung tập (⑤), từ `script.qualityReview` (biên lai lúc SINH tập). Ghi "Lúc viết" vì biên lai KHÔNG tự cập nhật khi người sửa tay / áp phương án sau đó.
 * Không hiện gì khi bản cũ không có biên lai hoặc `defectsBefore = 0`. Có lỗi mà không có lượt sửa (không có cảnh đích) vẫn nói thật số lỗi — không im lặng.
 */
export const qualityReviewLine = (rec: Pick<EpisodeScriptRecord, 'script'> | null | undefined): string | null => {
  const ig = rec?.script?.qualityReview?.integrity;
  if (!ig || !Number.isFinite(ig.defectsBefore) || ig.defectsBefore <= 0) return null;
  const after = Number.isFinite(ig.defectsAfter) ? ig.defectsAfter : ig.defectsBefore;
  const repair = ig.repair;
  if (repair?.accepted) {
    const n = Array.isArray(repair.changedScenes) ? repair.changedScenes.length : 0;
    return `Lúc viết: tự kiểm đã sửa ${n} cảnh (nhịp trống / thoại thiếu người nói).`;
  }
  return `Lúc viết: tự kiểm ${repair ? 'không sửa được' : 'thấy lỗi nhịp mà không có cảnh để sửa'} — còn ${after} lỗi, dùng trợ lý.`;
};
